/**
 * Claude API に送るプロンプト定義 v0.2
 *
 * v0.1 では自由形式テキストを返してもらっていたが、v0.2 では UI が
 * ORIGINAL / JAPANESE / CONF / LANG の4要素に分かれているので、
 * 構造化された JSON レスポンスを返してもらう。
 *
 * パース失敗時は claude.ts 側で「テキストフォールバック」して
 * 旧 v0.1 互換の表示に落とすので、AI が JSON を返さなくても致命的でない。
 */

import type { Mode } from '../types';

/**
 * 画像解析プロンプトを返すモード（'interpret' は音声プロンプトのため別関数で扱う）
 */
export type VisionMode = Exclude<Mode, 'interpret'>;

/** 翻訳モード（和訳） */
const TRANSLATE_PROMPT = `この画像を解析し、以下のJSON形式だけを返してください。前置きや解説は禁止、コードブロックも不要、純粋なJSONテキストのみ返答してください。

{
  "original": "画像内の文字を改行・記号も含めできる限り忠実に書き起こした原文。読み取れない場合は空文字。",
  "japanese": "originalの自然な日本語訳。元から日本語の場合はそのままoriginalを再掲。読み取れない場合は「[文字が見つかりません]」。",
  "sourceLang": "元の言語のISO 639-1コード小文字（en/fr/zh/ja等）。判定不可なら 'unknown'。",
  "confidence": 0から100の整数（読み取り＋翻訳の自己評価信頼度）
}

ルール:
- 余計なフィールドを増やさない
- JSON以外の文字を一切返さない（"\`\`\`json" のようなフェンスも不要）
- originalは元の言語のまま、改変しない`;

/** 要約モード */
const SUMMARIZE_PROMPT = `この画像を解析し、以下のJSON形式だけを返してください。前置きや解説は禁止、コードブロックも不要、純粋なJSONテキストのみ返答してください。

{
  "original": "画像に写っているものを中立的に2-3文で記述した内容（文章なら主旨、風景なら被写体、図表なら構成）。",
  "japanese": "上記をふまえた日本語の要点。文字主体なら箇条書き3-5個（・で開始、改行区切り）。風景・物体なら1-2文の説明。図表なら主要な数値・傾向。認識できない場合は「[認識できませんでした]」。",
  "sourceLang": "originalで使った主要言語のISO 639-1コード小文字。日本語ならja、英語混在ならsourceの主言語。判定不可ならunknown。",
  "confidence": 0から100の整数（要約の自己評価信頼度）
}

ルール:
- 余計なフィールドを増やさない
- JSON以外の文字を一切返さない（"\`\`\`json" のようなフェンスも不要）`;

/**
 * 画像解析モード（translate / summarize）のプロンプトを返す。
 * interpret モードは音声テキストの英→日翻訳プロンプトを別関数で扱う。
 */
export function getPromptForMode(mode: VisionMode): string {
  switch (mode) {
    case 'translate':
      return TRANSLATE_PROMPT;
    case 'summarize':
      return SUMMARIZE_PROMPT;
  }
}

/**
 * 通訳モードの翻訳プロンプト（英→日）
 *
 * 要件:
 *   - 自然で会話的な日本語
 *   - 前置き・解説なし、訳文のみ
 *   - コードブロック・原文の繰り返しなし
 *
 * テキスト1セグメント分の翻訳を依頼するため、画像系プロンプトとは別系統。
 */
export const INTERPRET_PROMPT = `You are an interpreter who translates spoken English into natural Japanese.
The user is in a real conversation - keep translations conversational and concise.
Do not add explanations, only return the Japanese translation.
Do not include the original English in your response.
Do not wrap your response in code blocks or quotes.`;
